<template>
  <div class="gk-range-input" :class="['gk-range-input--' + theme]">

    <div class="range-ruler" :class="{ 'unselected': localValue == 0}" :style="cssVars">
      <p v-for="item in steps" :key="item" class="range-ruler__label" :class="{'selected': (localValue == item) }">
        <span @click="localValue = item" class="ruler-text">{{ item }}</span><span class="ruler-mark"></span>
      </p>
      <p v-if="showMore" class="range-ruler__label" :class="{'selected': (localValue == compSteps) }">
        <span @click="localValue = compSteps" class="ruler-text" >{{ $gettext('More') }}</span><span class="ruler-mark"></span>
      </p>
    </div>
    <input v-model="localValue" ref="gkRangeInput" class="teamsize-input" type="range" :min="min" :max="compSteps" :step="step" />
    <span :style="compTrackWidth" class="gk-range-input__track-lower"></span>
  </div>
</template>

<script>
export default {
  name: "GekkoRangeInput",
  props: {
    value: {
      type: [Number, String],
      required: true
    },
    steps: {
      type: Number,
      default: 0
    },
    min: {
      type: Number,
      default: 0
    },
    step: {
      type: Number,
      default: 1
    },
    showMore: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'brand'
    }
  },
  data () {
    return {
      inputWidth: null
    }
  },
  computed: {
    localValue: {
      get () { return this.value },
      set (localValue) { this.$emit('input', localValue)  }
    },
    compTrackWidth () {
      const buttonWidth = 24
      const centerDotOffset = (this.localValue / this.compSteps - 0.5) * buttonWidth
      const off = this.inputWidth / this.compSteps
      const trackWidth = ((this.localValue - parseInt(this.min)) * off) - centerDotOffset
      return 'width: ' + trackWidth + 'px'
    },
    compSteps () {
      return (this.showMore) ? this.steps + 1 : this.steps
    },
    cssVars () {
      return {
        '--steps': this.steps,
      }
    }
  },
  created () {
    window.addEventListener('resize', this.handleResize);
  },
  destroyed () {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted () {
    this.handleResize()
  },
  methods: {
    handleResize () {
      this.inputWidth = (this.$refs.gkRangeInput) ? this.$refs.gkRangeInput.clientWidth : 0
    }
  },
}
</script>

<style lang="scss" scoped>
  .gk-range-input {
    $inputWidth: 100%;
    
    $trackHeight: 0.5rem;
    $thumbSize: calc( #{$trackHeight} * 3);

    $inputPadding: 1rem;
    $inputHeight: calc( #{$inputPadding} + #{$trackHeight});

    $labelHeight: calc( #{$trackHeight} * 3);

    $thumbBorderSize: 4px;

    $trackRadius: $radiusMd;
    $thumbRadius: $radiusXl;
  
    display: flex;
    flex-direction: column;
    width: $inputWidth;
    .teamsize-input {
      height: $inputHeight;
    }
    
    .range-ruler {
      display: flex;
      padding: 0px calc(#{$thumbSize} / 2) 0px calc(#{$thumbSize} / 2); // half thumb button width

      &__label {
        margin: 0;
        line-height: $labelHeight;
        font-size: 1rem;
        width: calc(#{ $inputWidth } / var(--steps));
        height: $labelHeight;
        position: relative;
        &:first-child:before {
          content: '0';
          position: absolute;
          top: 0;
          left: 0;
          line-height: $labelHeight;
          transform: translateX(-50%);
        }
        .ruler-text, .ruler-mark {
          position: absolute;
          right: 0;
          z-index: 200;
          transform: translateX(50%);
          font-size: 1rem;
          top: 0;
        }
        &:first-child:before, .ruler-text, .ruler-mark { font-size: 1rem; }
        @media (max-width: '576px') {
          &:first-child:before, .ruler-text, .ruler-mark { font-size: 13px; }
        }

        .ruler-text { cursor: pointer; user-select: none; }
        .ruler-mark {
          height: $trackHeight;
          width: 1px;
          margin-top: calc( #{ $labelHeight } + calc(#{ $inputPadding } / 2));
          pointer-events: none;
        }
      }

      &.unselected,
      .range-ruler__label.selected ~ .range-ruler__label {
        .ruler-mark { background-color: $colorUnassigned; }
      }

    }

    &__track-lower {
      display: block;
      position: relative;
      height: $trackHeight;
      margin-top: calc(calc(#{ $trackHeight } + calc(#{ $inputPadding } / 2)) * -1);
      pointer-events: none;
      border-radius: $trackRadius;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      transition: width 0.2s ease;
    }

    @-moz-document url-prefix() {
      .gk-range-input__track-lower {
        display: none;
      }
      .range-ruler {
        &__label {
          .ruler-mark {
            z-index: unset !important;
          }
        }
      }
    }

    /********** 
      RANGE INPUT STYLES
    **********/

    /* Range Reset */
    input[type="range"] {
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      cursor: pointer;
      width: 100%;
    }
    /* Removes default focus */
    input[type="range"]:focus { outline: none; }

    /***** Chrome, Safari, Opera and Edge Chromium styles *****/
    /* slider track */
    input[type="range"]::-webkit-slider-runnable-track {
      border-radius: $trackRadius;
      height: $trackHeight;  
    }
    /* slider thumb */
    input[type="range"]::-webkit-slider-thumb {
      -webkit-appearance: none; /* Override default look */
      appearance: none;
      cursor: pointer;
      box-sizing: border-box;
      /* custom styles */
      position: relative;
      z-index: 300;
      margin-top: calc(#{ $trackHeight } * -1);
      height: $thumbSize;
      width: $thumbSize;
      border-radius: $thumbRadius;
      box-shadow: none;
    }
    input[type="range"]:focus::-webkit-slider-thumb {   
      outline: 0;
      box-shadow: none;
    }

    /******** Firefox styles ********/

    /* slider track */
    input[type="range"]::-moz-range-track,
    input[type="range"]::-moz-range-progress {
      border-radius: $trackRadius;
      height: $trackHeight;
    }
    /* slider thumb */
    input[type="range"]::-moz-range-thumb {
      border-radius: $thumbRadius;
      height: calc(#{$thumbSize} - calc(#{$thumbBorderSize} * 2));
      width: calc(#{$thumbSize} - calc(#{$thumbBorderSize} * 2));
      position: relative;
      z-index: 300;
      transform: translateZ(3px);
    }
    input[type="range"]:focus::-moz-range-thumb {
      outline: 0px;
      box-shadow: none;
    }
    
    /******** THEMES ********/

    /* for light backgrounds (default) */
    &--brand { 
      $progressColor: $colorBrand;
      $runnableColor: white;
      $thumbBgColor: white;
      $thumbBorder: $thumbBorderSize solid $colorBrand;
      $thumbBorderFocus: $thumbBorderSize solid $colorBrand800;

      $rulerLabelColor: $colorUnassigned;
      $rulerSelectedColor: $colorBrand800;
      $rulerMarkColor: #ffffff8a;
      $rulerNextMarkColor: $colorUnassigned200;

      .range-ruler {
        .range-ruler__label {
          color: $rulerLabelColor;
          &.selected { .ruler-text { color: $rulerSelectedColor; } }
          .ruler-mark { background-color: $rulerMarkColor; }
        }
        &.unselected,
        .range-ruler__label.selected ~ .range-ruler__label {
          .ruler-mark { background-color: $rulerNextMarkColor; }
        }
      }
      .gk-range-input__track-lower { background-color: $progressColor; }
      input[type="range"]::-moz-range-progress { background-color: $progressColor; }
      input[type="range"]::-webkit-slider-runnable-track { background-color: $runnableColor; outline: 1px solid rgba(36, 172, 138, 0.2); }
      input[type="range"]::-moz-range-track { background-color: $runnableColor; outline: 1px solid rgba(36, 172, 138, 0.2); }
      input[type="range"]::-webkit-slider-thumb { border: $thumbBorder; background-color: $thumbBgColor; }
      input[type="range"]::-moz-range-thumb { border: $thumbBorder; background-color: $thumbBgColor; }
      input[type="range"]:focus::-webkit-slider-thumb { border: $thumbBorderFocus; }
      input[type="range"]:focus::-moz-range-thumb { border: $thumbBorderFocus; }
    }

    /* for dark backgrounds */
    &--light {
      $progressColor: $colorBrand800;
      $runnableColor: white;
      $thumbBgColor: $colorBrand;
      $thumbBorder: $thumbBorderSize solid white;
      $thumbBorderFocus: $thumbBorderSize solid white;

      $rulerLabelColor: #ffffff8a;
      $rulerSelectedColor: white;
      $rulerMarkColor: #ffffff8a;
      $rulerNextMarkColor: $colorBrand;

      .range-ruler {
        .range-ruler__label {
          color: $rulerLabelColor;
          &.selected { .ruler-text { color: $rulerSelectedColor; } }
          .ruler-mark { background-color: $rulerMarkColor; }
        }
        &.unselected,
        .range-ruler__label.selected ~ .range-ruler__label {
          .ruler-mark { background-color: $rulerNextMarkColor; }
        }
      }
      .gk-range-input__track-lower { background-color: $progressColor; }
      input[type="range"]::-moz-range-progress { background-color: $progressColor; }
      input[type="range"]::-webkit-slider-runnable-track { background-color: $runnableColor; outline: 1px solid rgba(36, 172, 138, 0.2); }
      input[type="range"]::-moz-range-track { background-color: $runnableColor; outline: 1px solid rgba(36, 172, 138, 0.2); }
      input[type="range"]::-webkit-slider-thumb { border: $thumbBorder; background-color: $thumbBgColor; }
      input[type="range"]::-moz-range-thumb { border: $thumbBorder; background-color: $thumbBgColor; }
      input[type="range"]:focus::-webkit-slider-thumb { border: $thumbBorderFocus; }
      input[type="range"]:focus::-moz-range-thumb { border: $thumbBorderFocus; }   
    }
  }
</style>